/**
 * MovementCameraRecorder Component
 *
 * Full-screen camera recording dialog.
 * All shadcn Dialog/Button/Badge replaced with Tailwind.
 * Mode: Deep Analysis (Mode 3)
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Camera, Video, StopCircle, RotateCcw, Check, X } from 'lucide-react';
import { useWebcam } from '../../hooks/useWebcam';
import { useRecording } from '../../hooks/useRecording';

export const MovementCameraRecorder = ({ isOpen, onClose, onVideoRecorded }) => {
  const { videoRef, streamRef, isWebcamOn, startWebcam, stopWebcam } = useWebcam();
  const { isRecording, recordedChunks, mediaRecorderRef, startRecording, stopRecording } = useRecording();
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasRecording, setHasRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Handle recording chunks
  useEffect(() => {
    if (recordedChunks.length > 0 && !isRecording) {
      setHasRecording(true);
    }
  }, [recordedChunks, isRecording]);

  const handleStartCamera = async () => {
    try {
      setCameraError(null);
      await startWebcam();
    } catch (err) {
      setCameraError('Failed to start camera. Please check permissions.');
    }
  };

  const handleStartRecording = () => {
    if (streamRef.current) {
      startRecording(streamRef.current);
      setHasRecording(false);
    }
  };

  const handleStopRecording = () => {
    stopRecording();
  };

  const handleRetake = () => {
    setHasRecording(false);
    setRecordingTime(0);
  };

  const handleSaveRecording = useCallback(async () => {
    if (recordedChunks.length === 0) return;

    setIsProcessing(true);
    try {
      const mimeType = mediaRecorderRef.current?.mimeType || 'video/webm';
      const blob = new Blob(recordedChunks, { type: mimeType });
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const fileName = `movement-analysis-${timestamp}.${extension}`;
      const file = new File([blob], fileName, { type: mimeType });

      onVideoRecorded(file);
      onClose();
      stopWebcam();
    } catch (err) {
      console.error('Error processing recording:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [recordedChunks, mediaRecorderRef, onVideoRecorded, onClose, stopWebcam]);

  const handleClose = () => {
    stopWebcam();
    setHasRecording(false);
    setRecordingTime(0);
    setCameraError(null);
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-[#0b1118] border border-white/10 rounded-2xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-zinc-100">
            <Video className="h-5 w-5 text-orange-400" />
            <span className="font-medium">Record Movement Analysis Video</span>
          </div>
          <button onClick={handleClose} className="text-zinc-400 hover:text-zinc-200 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Camera Error */}
          {cameraError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {cameraError}
            </div>
          )}

          {/* Camera Preview */}
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
              autoPlay
              muted
              playsInline
            />

            {!isWebcamOn && (
              <div className="absolute inset-0 flex items-center justify-center text-zinc-400">
                <div className="text-center">
                  <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Camera not started</p>
                  <p className="text-sm text-zinc-600 mt-1">Click "Start Camera" to begin</p>
                </div>
              </div>
            )}

            {/* Recording Indicator */}
            {isRecording && (
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-xs font-mono text-red-400 bg-red-500/20 px-2 py-0.5 rounded-full">
                  REC {formatTime(recordingTime)}
                </span>
              </div>
            )}

            {/* Recording Controls Overlay */}
            {isWebcamOn && !hasRecording && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                {!isRecording ? (
                  <button
                    onClick={handleStartRecording}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full flex items-center gap-2 text-sm"
                  >
                    <Video className="h-5 w-5" /> Start Recording
                  </button>
                ) : (
                  <button
                    onClick={handleStopRecording}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-3 rounded-full flex items-center gap-2 text-sm"
                  >
                    <StopCircle className="h-5 w-5" /> Stop Recording
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Recording Tips */}
          {isWebcamOn && !isRecording && !hasRecording && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-sm text-blue-300">
                <strong>Recording Tips:</strong> Position yourself so your full movement is visible.
                Keep recordings under 60 seconds for optimal analysis. Ensure good lighting for best results.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              {!isWebcamOn ? (
                <button
                  onClick={handleStartCamera}
                  className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm flex items-center gap-2"
                >
                  <Camera className="h-4 w-4" /> Start Camera
                </button>
              ) : (
                <>
                  {hasRecording && (
                    <>
                      <button
                        onClick={handleRetake}
                        disabled={isProcessing}
                        className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-zinc-200 hover:bg-white/[0.08] flex items-center gap-2 disabled:opacity-50"
                      >
                        <RotateCcw className="h-4 w-4" /> Retake
                      </button>
                      <button
                        onClick={handleSaveRecording}
                        disabled={isProcessing}
                        className="rounded-xl bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                      >
                        <Check className="h-4 w-4" />
                        {isProcessing ? 'Processing...' : 'Use This Recording'}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>

            <button
              onClick={handleClose}
              className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-zinc-200 hover:bg-white/[0.08] flex items-center gap-2"
            >
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>

          {/* Recording Info */}
          {hasRecording && (
            <div className="text-sm text-zinc-500 text-center">
              Recording ready | Duration: {formatTime(recordingTime)} |
              Size: {recordedChunks.length > 0 ? `${(recordedChunks.reduce((acc, chunk) => acc + chunk.size, 0) / 1024 / 1024).toFixed(1)}MB` : 'Unknown'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
